import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Spin } from 'antd';
import { Icon, Input } from 'Components';
import styles from './FilterSearch.module.scss';

export type FilterOptionId = number | string;

export interface FilterOption {
	id: FilterOptionId;
	label: string;
	desc?: string;
}

const LABEL_MAP: Record<string, string> = {
	roles: 'Role',
	property: 'Property',
	individual: 'Individual'
};

interface Props {
	filterType: string;
	options: FilterOption[];
	selectedIds: FilterOptionId[];
	onChange: (ids: FilterOptionId[]) => void;
	loading?: boolean;
	hasMore?: boolean;
	loadingMore?: boolean;
	onLoadMore?: () => void;
	onSearch?: (query: string) => void;
}

const LOAD_MORE_THRESHOLD = 24;

const FilterSearch: React.FC<Props> = ({
	filterType,
	options,
	selectedIds,
	onChange,
	loading,
	hasMore,
	loadingMore,
	onLoadMore,
	onSearch
}) => {
	const label = LABEL_MAP[filterType];
	const [search, setSearch] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const fieldRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const knownOptions = useRef(new Map<FilterOptionId, FilterOption>());
	const knownOptionsType = useRef(filterType);
	const isServerSearch = !!onSearch;

	if (knownOptionsType.current !== filterType) {
		knownOptionsType.current = filterType;
		knownOptions.current = new Map();
	}

	useEffect(() => {
		setSearch('');
		setIsOpen(false);
	}, [filterType]);

	useEffect(() => {
		onSearch?.(search);
		if (dropdownRef.current) dropdownRef.current.scrollTop = 0;
	}, [search]);

	options.forEach((option) => knownOptions.current.set(option.id, option));

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				fieldRef.current &&
				!fieldRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside, true);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside, true);
	}, []);

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query || isServerSearch) return options;
		return options.filter(
			(option) =>
				option.label.toLowerCase().includes(query) ||
				(option.desc ?? '').toLowerCase().includes(query)
		);
	}, [options, search, isServerSearch]);

	const canLoadMore = !!onLoadMore && !!hasMore && !loading && !loadingMore;

	const loadMoreIfNearBottom = () => {
		const dropdown = dropdownRef.current;
		if (!dropdown || !canLoadMore) return;
		const remaining =
			dropdown.scrollHeight - dropdown.scrollTop - dropdown.clientHeight;
		if (remaining <= LOAD_MORE_THRESHOLD) onLoadMore?.();
	};

	useEffect(() => {
		if (isOpen) loadMoreIfNearBottom();
	}, [isOpen, canLoadMore, filtered.length]);

	const selectedOptions = useMemo(
		() =>
			selectedIds
				.map((id) => knownOptions.current.get(id))
				.filter((option): option is FilterOption => !!option),
		[options, selectedIds]
	);

	if (!label) return null;

	const lowerLabel = label.toLowerCase();

	const toggleOption = (id: FilterOptionId) => {
		onChange(
			selectedIds.includes(id)
				? selectedIds.filter((selectedId) => selectedId !== id)
				: [...selectedIds, id]
		);
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.label}>Select {lowerLabel}</div>
			<div className={styles.field} ref={fieldRef}>
				<Input.Search
					id={`lms-ca-filter-${filterType}-search`}
					className={styles.input}
					placeholder={`Search ${lowerLabel}`}
					suffix={<Icon type="search" role="img" />}
					allowClear
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onFocus={() => setIsOpen(true)}
				/>
				{isOpen && (
					<div
						id={`lms-ca-filter-${filterType}-options`}
						className={styles.dropdown}
						ref={dropdownRef}
						onScroll={loadMoreIfNearBottom}
					>
						{loading && (
							<div className={styles.dropdown__status}>
								<Spin size="small" />
							</div>
						)}
						{!loading && !hasMore && filtered.length === 0 && (
							<div className={styles.dropdown__status}>
								No {lowerLabel} found
							</div>
						)}
						{!loading &&
							filtered.map((option) => (
								<label
									key={option.id}
									className={classNames(styles.option, {
										[styles['option--selected']]: selectedIds.includes(
											option.id
										)
									})}
								>
									<input
										type="checkbox"
										className={styles.option__checkbox}
										checked={selectedIds.includes(option.id)}
										onChange={() => toggleOption(option.id)}
									/>
									<span className={styles.option__label}>
										{option.label}
									</span>
								</label>
							))}
						{!loading && hasMore && (
							<div className={styles.dropdown__status}>
								<Spin size="small" />
							</div>
						)}
					</div>
				)}
			</div>
			{selectedOptions.length > 0 && (
				<div className={styles.selection}>
					{selectedOptions.map((option) => (
						<span key={option.id} className={styles.chip}>
							{option.label}
							<button
								type="button"
								className={styles.chip__remove}
								aria-label={`Remove ${option.label}`}
								onClick={() => toggleOption(option.id)}
							>
								×
							</button>
						</span>
					))}
					<button
						type="button"
						className={styles.clear}
						onClick={() => onChange([])}
					>
						Clear all
					</button>
				</div>
			)}
		</div>
	);
};

export default FilterSearch;
